// Widget instance adapter api module — the generator-facing surface for the
// dashboard-v2 Widget Studio instance screens.
//
// CHG-2460 / ITER-905202 — Widget Studio S3 (instances CRUD).
//
// The v2 screen generator (tools/v2-gen/v2gen.py) emits
// generated/screens/widget-instance-{list,detail}.ts whose queryFns call
// `widgetInstancesApi.listWidgetInstances({page,pageSize,sortField,
// sortDirection,search,filters})` and `widgetInstancesApi.getWidgetInstance(id)`
// — the same ListPage/DetailPage queryFn contract every other v2 entity uses
// (donors.ts / campaigns.ts). The landed typed admin client (widgets-admin.ts)
// has a different param shape (limit/offset, no page/search) and no GET-by-id
// route, so this thin adapter maps between the two — keeping widgets-admin.ts
// the single source of HTTP paths/wire shapes while conforming to the
// generator's naming/contract with zero generator changes.

import { widgetsAdminApi } from "./widgets-admin";
import type { WidgetActivationStatus, WidgetRegistrationStatus } from "./widgets-admin";
import type { WidgetInstance } from "./types";

export interface ListWidgetInstancesParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  // Opaque filter dict the v2 ListPage filterChips slot forwards. Keys match
  // the entity-hint filter_chip[].field (activation_status / registration_status).
  filters?: Record<string, string | number | boolean | undefined>;
}

// GET /admin/widgets/instances mapped to the ListPage contract.
//
// NOTE: `search` and `sortField`/`sortDirection` are accepted to satisfy the
// generator queryFn shape but are forward-promised — the admin list endpoint
// does not yet expose a free-text/sort param, so they are not forwarded today.
// The search box + sortable columns render (per the entity-hint) and become
// live the moment the BE adds the params, with no FE change. Filter chips
// (activation_status / registration_status) ARE wired through.
export async function listWidgetInstances(
  params: ListWidgetInstancesParams = {},
): Promise<{ items: WidgetInstance[]; total: number }> {
  const pageSize = params.pageSize ?? 50;
  const page = params.page ?? 0;
  const f = params.filters ?? {};
  const env = await widgetsAdminApi.listInstances({
    activation_status: f.activation_status as WidgetActivationStatus | undefined,
    registration_status: f.registration_status as WidgetRegistrationStatus | undefined,
    limit: pageSize,
    offset: page * pageSize,
  });
  return { items: env.items, total: env.total };
}

// There is NO GET-by-id route on the admin router (see widgets-admin.ts). The
// instance detail resolves the row by scanning the org list, then merges the
// analytics/summary rollup counters so the detail status cards (Impressions /
// Viewable / Clicks — sourced from the rollup, NOT instance columns) populate.
// A dedicated GET /admin/widgets/instances/{id} is the forward-promised BE
// follow-up; swap this scan for a direct fetch when it lands.
export async function getWidgetInstance(id: string): Promise<WidgetInstance> {
  const [env, summary] = await Promise.all([
    widgetsAdminApi.listInstances({ limit: 200, offset: 0 }),
    widgetsAdminApi.analyticsSummary(id).catch(() => null),
  ]);
  const found = env.items.find((i) => i.id === id);
  if (!found) {
    throw new Error(`Widget instance ${id} not found`);
  }
  return {
    ...found,
    impressions: summary?.impressions,
    viewable_impressions: summary?.viewable_impressions,
    cta_clicks: summary?.cta_clicks,
  };
}

export const widgetInstancesApi = {
  listWidgetInstances,
  getWidgetInstance,
};
